/**
 * Rename one workspace item in a dialog: the phone views' rename, which have
 * no inline name editor (desktop views and the sidebar tree rename in place
 * with InlineRenameField). Saves through the same `renameItem` calls and
 * checks, and opens with the same base-name selection.
 */

import { useRef, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import type { WorkspaceItem } from '@bower/shared';

import { useAppToast } from '../../../hooks/ui/useAppToast';
import { baseNameEnd, renameItem } from '../rename/renameItem';

interface RenameItemDialogProps {
  /** The item to rename; null = closed. */
  readonly item: WorkspaceItem | null;
  readonly onClose: () => void;
  readonly onRenamed: () => void;
}

export function RenameItemDialog({ item, onClose, onRenamed }: RenameItemDialogProps) {
  const toast = useAppToast();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  /** Counts opens; the base name is selected once per open, on first focus. */
  const [openCount, setOpenCount] = useState(0);
  const selectedOnOpen = useRef(0);
  // Start from the item's name each time the dialog opens for it.
  if ((item?.id ?? null) !== openedFor) {
    setOpenedFor(item?.id ?? null);
    if (item) {
      setName(item.name);
      setOpenCount((n) => n + 1);
    }
  }
  const selectBaseName = (input: HTMLInputElement) => {
    if (!item || selectedOnOpen.current === openCount) return;
    selectedOnOpen.current = openCount;
    input.setSelectionRange(0, baseNameEnd(item));
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!item || !trimmed || trimmed === item.name) return onClose();
    setIsSaving(true);
    const result = await renameItem(item, trimmed);
    setIsSaving(false);
    if (!result.ok) {
      toast({ title: 'Rename failed', description: result.error, status: 'error', duration: 3000 });
      return;
    }
    onRenamed();
    onClose();
  };

  return (
    <Modal isOpen={item !== null} onClose={onClose} initialFocusRef={inputRef} isCentered>
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <ModalHeader>Rename</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={(e) => selectBaseName(e.currentTarget)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" variant="primary" isLoading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
